'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { reviewService, type ProductReview, type ReviewStats } from '@/lib/services/reviewService';
import { useAuth } from '@/contexts/AuthContext';
import ReviewSubmissionModal from './ReviewSubmissionModal';

interface ReviewsSectionProps {
  productId: string;
  productName: string;
}

export default function ReviewsSection({ productId, productName }: ReviewsSectionProps) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      loadReviews();
    }
  }, [productId, isHydrated]);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const [reviewsData, statsData] = await Promise.all([
        reviewService.getProductReviews(productId),
        reviewService.getProductReviewStats(productId),
      ]);
      setReviews(reviewsData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSuccess = () => {
    loadReviews();
  };

  const handleMarkHelpful = async (reviewId: string) => {
    if (!user) return;
    try {
      await reviewService.markReviewHelpful(reviewId, 'product');
      loadReviews();
    } catch (error) {
      console.error('Failed to mark review as helpful:', error);
    }
  };

  if (!isHydrated) {
    return (
      <div className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-heading font-bold text-foreground mb-8">Customer Reviews</h2>
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="h-32 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-heading font-bold text-foreground">Customer Reviews</h2>
            {user && (
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:shadow-elevation-md transition-smooth"
              >
                <Icon name="PencilIcon" size={20} />
                Write a Review
              </button>
            )}
          </div>

          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-32 bg-muted rounded-lg"></div>
              <div className="h-32 bg-muted rounded-lg"></div>
            </div>
          ) : (
            <>
              {stats && stats.totalReviews > 0 && (
                <div className="bg-card border border-border rounded-xl p-6 mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="text-5xl font-heading font-bold text-foreground mb-2">
                        {stats.averageRating.toFixed(1)}
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Icon
                            key={star}
                            name="StarIcon"
                            size={20}
                            className={`${
                              star <= Math.round(stats.averageRating)
                                ? 'text-yellow-400 fill-yellow-400' :'text-muted-foreground'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Based on {stats.totalReviews} {stats.totalReviews === 1 ? 'review' : 'reviews'}
                      </p>
                    </div>

                    <div className="space-y-2">
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <div key={rating} className="flex items-center gap-3">
                          <span className="text-sm font-medium text-foreground w-8">{rating} ★</span>
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-yellow-400"
                              style={{
                                width: `${((stats.ratingDistribution[rating] || 0) / stats.totalReviews) * 100}%`,
                              }}
                            ></div>
                          </div>
                          <span className="text-sm text-muted-foreground w-8">
                            {stats.ratingDistribution[rating] || 0}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col justify-center items-center">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="ShieldCheckIcon" size={24} className="text-success" />
                        <span className="text-2xl font-heading font-bold text-foreground">
                          {stats.verifiedPurchasePercentage}%
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground text-center">
                        Verified Purchases
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {reviews.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg text-muted-foreground mb-4">No reviews yet</p>
                  {user && (
                    <button
                      onClick={() => setShowModal(true)}
                      className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:shadow-elevation-md transition-smooth"
                    >
                      Be the first to review
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="bg-card border border-border rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Icon
                                  key={star}
                                  name="StarIcon"
                                  size={16}
                                  className={`${
                                    star <= review.rating
                                      ? 'text-yellow-400 fill-yellow-400' :'text-muted-foreground'
                                  }`}
                                />
                              ))}
                            </div>
                            {review.isVerifiedPurchase && (
                              <span className="flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                                <Icon name="ShieldCheckIcon" size={14} />
                                Verified Purchase
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-foreground mb-1">{review.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            By {review.reviewerName} • {new Date(review.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <p className="text-foreground mb-4 leading-relaxed">{review.reviewText}</p>

                      <div className="flex items-center gap-4 pt-4 border-t border-border">
                        <button
                          onClick={() => handleMarkHelpful(review.id)}
                          disabled={!user}
                          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="HandThumbUpIcon" size={16} />
                          Helpful ({review.helpfulCount})
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && (
        <ReviewSubmissionModal
          productId={productId}
          productName={productName}
          onClose={() => setShowModal(false)}
          onSuccess={handleReviewSuccess}
        />
      )}
    </>
  );
}